import React, {useEffect, useMemo, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import './Dropzone.scss';
import {ReactComponent as UploadIcon} from '../../../assets/icons/upload.svg';
import {ReactComponent as RemoveIcon} from '../../../assets/icons/remove.svg';
import {
  acceptGeneralFormats,
  concatFileExtension,
  convertSizeToMB,
  convertSizeUi,
  getIconForFormat,
  urlToFile
} from '../../../helpers/functions';
import {notifyError} from '../../../helpers/notifySnack';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';

const Dropzone = ({
  onFilesAdded,
  maxSizeMb = 50,
  maxFiles = 1,
  allowFormats = ['.jpg', '.jpeg', '.png', '.pdf', '.docx'],
  initialImageUrl = '',
  disabled = false
}) => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const {t} = useTranslation();

  const getFileExtension = (filename) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  };

  const maxSizeValidator = (file) => {
    if (!file) return;
    const fileExtension = getFileExtension(file.path || file.name);

    if (!allowFormats.includes(`.${fileExtension}`)) {
      notifyError(`${t('DropzoneMultiple.fileFormatError')}`);
      return {
        code: 'invalid format'
      };
    }

    if (file && convertSizeToMB(file.size) > maxSizeMb) {
      notifyError(`${t('DropzoneMultiple.fileSize')} ${maxSizeMb}MB`);
      return {
        code: 'large size'
      };
    }
    return null;
  };

  const onDrop = (newFiles) => {
    const files = [...acceptedFiles, ...newFiles].slice(0, maxFiles);
    setAcceptedFiles(files);
    onFilesAdded(files);
  };

  const removeFile = (fileToRemove) => {
    if (disabled) return;
    const files = acceptedFiles.filter(
      (file) => file.path !== fileToRemove.path && file.lastModified !== fileToRemove.lastModified
    );
    setAcceptedFiles(files);
    onFilesAdded(files);
  };

  const onDropRejected = (e) => {
    const hasTooManyFilesError = e.some(({errors}) => errors.some((error) => error.code === 'too-many-files'));

    if (hasTooManyFilesError) {
      notifyError(`${t('Dropzone.sizes')} ${maxFiles}`);
    }
  };

  const filteredFormats = useMemo(() => {
    return Object.fromEntries(
      Object.entries(acceptGeneralFormats).filter(([key, extensions]) =>
        extensions.some((ext) => allowFormats.includes(ext))
      )
    );
  }, [acceptGeneralFormats, allowFormats, t]);

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    onDropRejected: onDropRejected,
    validator: maxSizeValidator,
    accept: filteredFormats
  });

  const isDisabled = acceptedFiles.length >= maxFiles || disabled;

  const files = acceptedFiles.map((file, idx) => (
    <div className={'dropzone-item'} key={file.path + '' + idx}>
      <div className='dropzone-details'>
        <div className='dropzone-img'>
          <img src={getIconForFormat(file.name.split('.').pop())} alt={file.name} />
        </div>
        <div className='dropzone-info'>
          <p className={'dropzone-name'}>{concatFileExtension(file.name)}</p>
          <p className={'dropzone-size'}>{convertSizeUi(file.size)}</p>
        </div>
      </div>
      <div className='dropzone-remove' onClick={() => removeFile(file)}>
        <RemoveIcon />
      </div>
    </div>
  ));

  useEffect(() => {
    const setInitialFiles = async () => {
      try {
        if (initialImageUrl) {
          const imageFile = await urlToFile(initialImageUrl?.path, initialImageUrl.name ?? 'initialImage');
          imageFile.preview = URL.createObjectURL(imageFile);
          setAcceptedFiles([imageFile]);
          onFilesAdded([imageFile]);
        }
      } catch (error) {}
    };

    setInitialFiles();
  }, [initialImageUrl]);

  return (
    <div className={'dropzone-wrapper'}>
      <div
        {...getRootProps({
          className: `dropzone ${isDisabled ? 'disabled' : ''}`,
          accept: '.pdf',
          onClick: (event) => {
            isDisabled && event.stopPropagation();
          }
        })}
      >
        <input {...getInputProps()} />
        <div className={`dropzone-content`}>
          <UploadIcon />
          <p className='dropzone-text'>{t('Dropzone.dropText')}</p>
        </div>
      </div>
      <div className='dropzone-notation'>
        {t('Dropzone.allowedFormats')}: {allowFormats.join(', ')}&nbsp;
        {t('Dropzone.maxSize')} {maxSizeMb} MB <br />
        {t('Dropzone.size')} {maxFiles}
      </div>
      <div className={'dropzone-files'}>
        <div className={'dropzone-list'}>{files}</div>
      </div>
    </div>
  );
};

export default Dropzone;

Dropzone.propTypes = {
  maxSizeMb: PropTypes.number,
  maxFiles: PropTypes.number,
  dropText: PropTypes.string,
  onFilesAdded: PropTypes.func,
  allowFormats: PropTypes.array
};
